import { type Preview, monthDay, rangeLabel } from "@/lib/gantt";
import { css } from "@styled-system/css";
import { WeekChangeStrip } from "./week-strip";

const panel = css({
  display: "flex",
  flexDir: "column",
  gap: "16",
  p: "16",
  border: "default",
  borderRadius: "4",
  bg: "surface",
  minW: "0",
});
const heading = css({
  fontFamily: "mono",
  fontSize: "[11px]",
  lineHeight: "[16px]",
  letterSpacing: "[0.04em]",
  textTransform: "uppercase",
  color: "text.subtlest",
  m: "0",
  mb: "6",
  fontWeight: "normal",
});
const body = css({ fontSize: "[13px]", lineHeight: "[18px]", color: "text", m: "0" });
const muted = css({ fontSize: "[13px]", lineHeight: "[18px]", color: "text.subtlest", m: "0" });
const ul = css({ listStyle: "none", m: "0", p: "0", display: "flex", flexDir: "column", gap: "4" });
const tag = css({ fontFamily: "mono", fontSize: "[11px]", color: "text.subtle", ml: "6" });
const tagNew = css({ fontFamily: "mono", fontSize: "[11px]", color: "text.warning", ml: "6" });

interface DragPreviewProps {
  preview: Preview | null;
}

/**
 * The drag preview: exactly four sections, in this order, and nothing else.
 * 1 new range · 2 📅 tasks now outside · 3 overlapping phases · 4 active phases per week.
 */
export function DragPreview({ preview }: DragPreviewProps) {
  if (!preview) {
    return (
      <aside className={panel} aria-label="Drag preview">
        <p className={muted}>Drag a bar, or select one, to see what the change does.</p>
      </aside>
    );
  }
  return (
    <aside className={panel} aria-label="Drag preview">
      <section aria-label="New range">
        <h2 className={heading}>New range</h2>
        <p className={body}>{preview.title}</p>
      </section>

      <section aria-label="Deadline tasks outside the phase">
        <h2 className={heading}>📅 tasks now outside</h2>
        {preview.outside.length === 0 ? (
          <p className={muted}>None.</p>
        ) : (
          <ul className={ul}>
            {preview.outside.map((m) => (
              <li key={m.taskId} className={body}>
                {m.text} · {monthDay(m.due)} · {m.side}
                {m.already && <span className={tag}>already outside</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Overlapping phases">
        <h2 className={heading}>Overlaps in this project</h2>
        {preview.overlaps.length === 0 ? (
          <p className={muted}>None.</p>
        ) : (
          <ul className={ul}>
            {preview.overlaps.map((o) => (
              <li key={o.id} className={body}>
                {o.name} · {rangeLabel(o.range)}
                {o.isNew && <span className={tagNew}>new</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Active phases per week">
        <h2 className={heading}>Active phases per week, all projects</h2>
        <WeekChangeStrip weeks={preview.weeks} />
      </section>
    </aside>
  );
}
